import { createMultipleFilter, createSingleFilter } from "app/utils/filters";

import type { GroupSectionsGridGroupsQuery } from "./__generated__/GroupSectionsGridGroupsQuery.graphql";

/** Which slice of a group's identity data an expanded row shows. */
export type GroupsView = "role-assignments" | "users";

/**
 * How a section selects its groups: one provider's namespace, all local
 * groups, or every group (optionally narrowed to a set of providers) for a
 * flat, ungrouped list.
 */
export type SectionScope =
  | { kind: "idp-namespace"; idpNamespace: string }
  | { kind: "local" }
  | {
      kind: "all";
      /** Providers to narrow to; empty or omitted lists every group. */
      idpNamespaces?: readonly string[];
    };

/** One section of the groups browser: a provider's groups, or the local ones. */
export interface GroupSection {
  /** Stable id (the provider's Relay id, or a fixed key for the local section). */
  id: string;
  /** Section label, e.g. "Okta" / "AWS" / "Local Groups". */
  label: string;
  /** Short secondary label after the section name (the namespace or org key). */
  secondary?: string | null;
  /** Selects the section's groups. */
  scope: SectionScope;
  /** Provider `idpType`, which picks the section header's icon; omit for the local section. */
  idpType?: string | null;
  /** Details-page link for the section's arrow action, or null when there is none. */
  to?: string | null;
  /** Edit-page link for the section's ✎ action, or null to omit it. */
  editTo?: string | null;
  /**
   * Omits the section's header row, so its groups read as a flat list. For the
   * single "every group" section of the ungrouped view.
   */
  hideHeader?: boolean;
}

/** The identity provider a group was provisioned from. */
export interface GroupProvider {
  /** Provider `idpType`, which picks the group's icon. */
  idpType: string;
  /** Human label for the provider, for the group's "Synced from" line. */
  label: string;
}

/** A group, denormalised for a group row. */
export interface Group {
  id: string;
  uuid: string;
  /** The group's stable name, which scopes its members list. */
  name: string;
  label: string;
  /** The provider the group was synced from, or null for a local group. */
  provider: GroupProvider | null;
  /** Direct member count, when the API reports one. */
  memberCount: number | null;
  /** Whether the group grants the admin role. */
  hasAdmin: boolean;
  /** Account groups the group's resource-viewer role is scoped to. */
  visibilityTargets: string[];
  /**
   * True when the group has more role assignments than were fetched (capped),
   * so the access summary and expanded table are incomplete.
   */
  hasMoreAssignments: boolean;
  /** The group node, handed to the expanded panel's role assignment table. */
  principalRef: NonNullable<
    NonNullable<
      GroupSectionsGridGroupsQuery["response"]["userGroups"]["edges"]
    >[number]
  >["node"];
}

/**
 * A provider's display label: its display name, else its alias, else its
 * name. Shared by the section headers and each group's "Synced from" line so
 * the two always agree.
 */
export function providerLabel(provider: {
  displayName?: string | null;
  idpAlias?: string | null;
  name: string;
}): string {
  return provider.displayName || provider.idpAlias || provider.name;
}

/**
 * Scopes the list to a section's groups, optionally narrowed by a search term
 * matched against the display name each row shows.
 *
 * Provider sections match by exact `idp-namespace` — its default `contains`
 * operator would otherwise leak groups from any provider whose namespace merely
 * contains this one. The local section matches `system: false`; `idp-namespace`
 * can't select local groups, as one with no provider matches no namespace.
 * The "all" scope has no scope filter unless narrowed to providers, which are
 * OR-ed together.
 */
export function buildGroupFilter(scope: SectionScope, search: string) {
  const term = search.trim();
  const searchFilter = term
    ? createSingleFilter("display-name", term, "contains")
    : undefined;

  if (scope.kind === "all") {
    const providers = createMultipleFilter(
      (scope.idpNamespaces ?? []).map((idpNamespace) =>
        createSingleFilter("idp-namespace", idpNamespace, "equals"),
      ),
      "OR",
    );
    return createMultipleFilter([providers, searchFilter]);
  }

  const scopeFilter =
    scope.kind === "local"
      ? createSingleFilter("system", "false")
      : createSingleFilter("idp-namespace", scope.idpNamespace, "equals");
  return createMultipleFilter([scopeFilter, searchFilter]);
}
